import { useMemo, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { API, endpoints } from '@/api/metrics';
import {
  type CatalogMetric,
  registerDynamicMetrics,
} from '@/modules/analytics/constants/metricsCatalog';
import { selectAccountId } from '@/redux/session/selectors';
import type { MetricsQueryRequest, MetricsQueryResponse } from '@/shared/types/metricsQuery';

const LOOKBACK_DAYS = 365;
const STALE_TIME_MS = 5 * 60 * 1000;

const CUSTOM_INSIGHTS_TABLE = 'ai_agents_custom_insights_daily';

// Shared shape for every dynamic custom-insight metric. The five metrics
// previously hard-coded in metricsCatalog.json all collapsed to this template;
// only `label` and `defaultFilter` (which scopes the CAGG to one insight) vary
// per insight. See useCustomInsightsCatalog below for how the variants are
// minted from the API response.
const CUSTOM_INSIGHT_TEMPLATE = {
  category: 'custom_insights',
  table: CUSTOM_INSIGHTS_TABLE,
  aggregations: [{ alias: 'total', column: 'conv_count', function: 'sum' as const }],
  groupBy: ['bucket_label', 'brain_parent_id', 'desk_id', 'channel'],
  primaryDimension: 'bucket_label',
  filters: [
    'brain_parent_id',
    'desk_id',
    'channel',
    'is_test',
    'is_covered',
    'is_proactive',
    'csat_brain',
  ],
  valueLabel: 'Conversations',
};

const buildCatalogMetric = (insightName: string, category: string): CatalogMetric => ({
  id: insightName,
  label: insightName,
  ...CUSTOM_INSIGHT_TEMPLATE,
  defaultFilter: `category eq '${category}' and insight_name eq '${insightName}'`,
});

const buildRequest = (): MetricsQueryRequest => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - LOOKBACK_DAYS);
  return {
    table: CUSTOM_INSIGHTS_TABLE,
    groupBy: ['insight_name', 'category'],
    aggregations: [{ function: 'sum', column: 'conv_count', alias: 'occurrences' }],
    timeRange: { start: start.toISOString(), end: end.toISOString() },
  };
};

export const useCustomInsightsCatalog = () => {
  const accountId = useSelector(selectAccountId);
  const request = useMemo(buildRequest, []);

  const query = useQuery<MetricsQueryResponse>({
    queryKey: [endpoints.query, accountId, 'customInsightsCatalog'],
    queryFn: () => API.query(request),
    enabled: !!accountId,
    staleTime: STALE_TIME_MS,
  });

  const metrics = useMemo<CatalogMetric[]>(() => {
    const rows = query.data?.data ?? [];
    return rows
      .filter(
        (row): row is { insight_name: string; category: string } =>
          typeof row.insight_name === 'string' && typeof row.category === 'string'
      )
      .map((row) => buildCatalogMetric(row.insight_name, row.category));
  }, [query.data]);

  // Register synchronously during render (not in useEffect) so that child
  // components mounted in the same React pass — MetricCombobox, MetricCard,
  // MetricEditor — see a warm registry on their first render. Effects fire
  // after commit, which would leave the picker rendering against an empty
  // registry and hiding the "Custom insights" category. The ref dedupes
  // re-registration to the same idempotent set per data identity.
  const lastRegisteredRef = useRef<CatalogMetric[] | null>(null);
  if (lastRegisteredRef.current !== metrics) {
    registerDynamicMetrics(metrics);
    lastRegisteredRef.current = metrics;
  }

  return { isLoading: query.isLoading, isError: query.isError, metrics };
};
